import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";
import { chartTheme } from "@/lib/chart-theme";
import { formatAmount, todayLocal } from "@/lib/utils";
import type { Goal, GoalContribution, GoalProgressPoint } from "@/api/types";

/**
 * График прогресса цели (SPEC-037): накопленный баланс цели по месяцам в
 * target_currency + линия target + маркер дедлайна + пунктирный ETA-прогноз.
 *
 * SPEC-054: ряд приходит с воркера (`progress_series`), посчитанный тем же движком,
 * что и сам баланс. Раньше компонент суммировал `delta_in_target` на клиенте и
 * опирался на равенство суммы балансу — в режиме mark_to_market это равенство
 * перестаёт выполняться (вклад переоценивается по текущему курсу, а delta_in_target
 * остаётся аудиторским следом на дату операции).
 *
 * Линия факта и темп ETA берутся из РАЗНЫХ источников намеренно. Линия — стоимость
 * фонда (`progress_series`, совпадает с `goal.balance`). Темп — приток новых вкладов
 * (`delta_in_target` операций), потому что ETA по SPEC-037 §ETA отвечает на вопрос
 * «сколько я откладываю в месяц». Разница соседних точек ряда включает курсовую
 * переоценку накопленного: в mark_to_market скачок курса читался бы как крупный
 * вклад, и прогноз даты становился фиктивным.
 *
 * Пунктир = forecast (memory dashed-line-means-forecast): пунктирная только
 * ETA-линия; target/deadline — тонкие solid-референсы.
 */

const MON = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const pad = (n: number) => String(n).padStart(2, "0");
const ymOf = (iso: string) => iso.slice(0, 7);
const cmpYm = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
const monthLabel = (ym: string) => { const [y, m] = ym.split("-"); return `${MON[+m - 1]} ’${y.slice(2)}`; };
const addMonthYm = (ym: string, d: number) => {
    const [y, m] = ym.split("-").map(Number);
    const dt = new Date(Date.UTC(y, m - 1 + d, 1));
    return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}`;
};
// «Сегодня» — из того же хелпера, которым клиент передаёт `?today=` воркеру (SPEC-024):
// день оценки на графике обязан совпадать с днём, по которому посчитан ряд.
const todayYm = () => ymOf(todayLocal());
const compact = (v: number) => {
    const a = Math.abs(v);
    if (a >= 1_000_000) return `${(v / 1_000_000).toFixed(a % 1_000_000 === 0 ? 0 : 1)}M`;
    if (a >= 1_000) return `${(v / 1_000).toFixed(a % 1_000 === 0 ? 0 : 1)}k`;
    return String(Math.round(v));
};

interface Model {
    months: string[];
    actual: (number | null)[];       // накопленный баланс по месяцам (null в будущем)
    target: number | null;
    deadlineYm: string | null;
    eta: (number | null)[] | null;   // пунктир-прогноз, состыкован с фактом
    etaEndYm: string | null;
    rate: number;                    // средний месячный приток (для подписи)
    etaTooFar: boolean;              // темп есть, но цель дальше горизонта → честная подпись без ложной даты
    etaBlockedByRates: boolean;      // последняя точка без курса → прогноз не строим, говорим об этом
    yMax: number;
    balanceNow: number;
}

const HORIZON_CAP_MONTHS = 120; // 10 лет — дальше ETA не рисуем линию-в-никуда с ложной датой (QA must-fix)

function buildModel(goal: Goal, series: GoalProgressPoint[], contributions: GoalContribution[]): Model | null {
    if (!series.length) return null;

    // Ряд уже накопительный и упорядоченный (воркер).
    const cum = new Map<string, number>();
    // Точка с неполной оценкой (нет курса) в график не идёт — показывать частичную
    // сумму как факт нельзя (E8); ряд в этом месте рвётся.
    const incomplete = new Set<string>();
    for (const p of series) {
        cum.set(p.month, p.balance);
        if (p.missing_rates > 0) incomplete.add(p.month);
    }

    // Приток по месяцам — из зафиксированных `delta_in_target` операций (аудиторский
    // след на дату вклада, одинаковый в обоих режимах). Строка без курса на свою дату
    // (delta_in_target = null) в темп не входит.
    const today = todayLocal();
    const inflowByMonth = new Map<string, number>();
    for (const c of contributions) {
        if (c.delta_in_target == null) continue;
        // Запланированный вклад (E9) остаётся в timeline, но в баланс не входит ни на
        // воркере, ни в ряде — в темпе он выдавал бы ещё не полученные деньги за приток.
        if (c.date > today) continue;
        const ym = ymOf(c.date);
        inflowByMonth.set(ym, (inflowByMonth.get(ym) ?? 0) + c.delta_in_target);
    }

    const withData = series.map(p => p.month);
    const firstYm = withData[0];
    const lastDataYm = withData[withData.length - 1];
    const histEnd = cmpYm(lastDataYm, todayYm()) >= 0 ? lastDataYm : todayYm();
    // Хвост правее ряда (локальный месяц клиента ушёл вперёд последней точки: вкладка,
    // открытая через смену месяца, или разница таймзон с воркером) рисуется её же
    // значением — значит, и неполноту наследует от неё: полнее источника он быть не может.
    // Иначе частичный `balanceNow` уходил бы в линию факта сплошным, как достоверный,
    // и `incomplete` не покрывал бы месяцы, по которым построен и стык прогноза.
    if (incomplete.has(lastDataYm)) {
        for (let ym = addMonthYm(lastDataYm, 1); cmpYm(ym, histEnd) <= 0; ym = addMonthYm(ym, 1)) incomplete.add(ym);
    }
    const target = goal.target_amount != null && goal.target_amount > 0 ? goal.target_amount : null;
    const deadlineYm = goal.deadline ? ymOf(goal.deadline) : null;

    // Месяцы факта: от первой точки ряда до текущего месяца (ряд воркера непрерывен,
    // цикл лишь достраивает хвост, если сегодня позже последней точки).
    const histMonths: string[] = [];
    for (let ym = firstYm; cmpYm(ym, histEnd) <= 0; ym = addMonthYm(ym, 1)) histMonths.push(ym);
    // Последняя точка ряда == goal.balance: обе величины считает один движок (AC14).
    const balanceNow = cum.get(lastDataYm) ?? 0;

    // ETA: средний приток за последние ≤3 ЗАВЕРШЁННЫХ месяца → линейная экстраполяция.
    // Текущий (неполный) календарный месяц исключаем — иначе его пустота занижает run-rate;
    // завершённые пустые месяцы остаются (реальный спад темпа их учитывает).
    let rate = 0;
    let etaTooFar = false;
    let etaBlockedByRates = false;
    const etaMonths: string[] = [];
    if (target != null && balanceNow < target) {
        // Прогноз стыкуется с крайней точкой факта (`histEnd`) и от неё же считает
        // расстояние до цели. Нет курса на неё — `balanceNow` частичен, и срок, и
        // якорь пунктира были бы выдуманными (дашборд по той же причине не тянет
        // проекцию от неполной точки). Частичный баланс всегда занижен, поэтому
        // `balanceNow < target` здесь ещё не значит, что цель не достигнута.
        // Признак неполноты один и тот же с линией факта — хвост его уже унаследовал.
        etaBlockedByRates = incomplete.has(histEnd);
        if (!etaBlockedByRates) {
            const cur = todayYm();
            const complete = histMonths.filter(ym => ym < cur);
            const tail = (complete.length ? complete : histMonths).slice(-3);
            rate = tail.reduce((s, ym) => s + (inflowByMonth.get(ym) ?? 0), 0) / (tail.length || 1);
            if (rate > 0) {
                const n = Math.ceil((target - balanceNow) / rate);
                if (n <= HORIZON_CAP_MONTHS) {
                    for (let i = 1; i <= n; i++) etaMonths.push(addMonthYm(histEnd, i));
                } else {
                    etaTooFar = true; // цель дальше горизонта — пунктир не доводим до target, подпись без ложной даты
                }
            }
        }
    }
    const etaEndYm = etaMonths.length ? etaMonths[etaMonths.length - 1] : null;

    // Ось X: [min(первый вклад, дедлайн) .. max(histEnd, дедлайн, ETA)] + буфер-месяц справа,
    // чтобы маркер дедлайна/ETA и последняя подпись не упирались в правый край (QA nice-to-have).
    let axisStart = firstYm;
    if (deadlineYm && cmpYm(deadlineYm, axisStart) < 0) axisStart = deadlineYm; // дедлайн раньше первого вклада (E7)
    let axisEnd = histEnd;
    if (deadlineYm && cmpYm(deadlineYm, axisEnd) > 0) axisEnd = deadlineYm;
    if (etaEndYm && cmpYm(etaEndYm, axisEnd) > 0) axisEnd = etaEndYm;
    axisEnd = addMonthYm(axisEnd, 1);
    const months: string[] = [];
    for (let ym = axisStart; cmpYm(ym, axisEnd) <= 0; ym = addMonthYm(ym, 1)) months.push(ym);

    // Факт только в [firstYm..histEnd]; вне (в т.ч. левее первого вклада при раннем дедлайне) — null.
    const actual = months.map(ym => {
        if (cmpYm(ym, firstYm) < 0 || cmpYm(ym, histEnd) > 0) return null;
        if (incomplete.has(ym)) return null;                      // неполная оценка — разрыв, не «факт»
        return Math.round(cum.get(ym) ?? balanceNow);             // вне ряда — только полный хвост (неполный отсеян выше)
    });

    let eta: (number | null)[] | null = null;
    if (etaMonths.length && target != null) {
        const etaIdx = new Map(etaMonths.map((ym, i) => [ym, i]));
        eta = months.map(ym => {
            if (ym === histEnd) return Math.round(balanceNow);      // стык с фактом
            const k = etaIdx.get(ym);
            return k == null ? null : Math.min(Math.round(balanceNow + rate * (k + 1)), Math.round(target));
        });
    }

    // SPEC-053: без нижней границы у цели без target_amount и отрицательным балансом
    // (правки, отрицательные delta_in_target) получался yMax < 0 при yAxis.min = 0 —
    // ECharts рисовал пустую или перевёрнутую область вместо графика прогресса.
    const yMax = Math.max(1, Math.ceil(Math.max(target ?? 0, balanceNow, 0) * 1.08));
    return { months, actual, target, deadlineYm, eta, etaEndYm, rate, etaTooFar, etaBlockedByRates, yMax, balanceNow };
}

export function GoalProgressChart({ goal, points, contributions }: {
    goal: Goal; points: GoalProgressPoint[]; contributions: GoalContribution[];
}) {
    const ccy = goal.target_currency;
    const model = useMemo(() => buildModel(goal, points, contributions), [goal, points, contributions]);

    // Гард (E2/E3): без валюты или без оценимых вкладов карточку не рендерим.
    if (!ccy || !model) return null;

    const t = chartTheme();
    const color = goal.color ?? t.positive;
    const fmt = (v: number) => `${formatAmount(v, ccy)} ${ccy}`;
    const { months, actual, target, deadlineYm, eta, etaEndYm, rate, etaTooFar, etaBlockedByRates, yMax } = model;

    // target/deadline — тонкие solid-референсы (НЕ пунктир: пунктир зарезервирован за forecast).
    const markLineData = [
        ...(target != null ? [{ yAxis: target, label: { formatter: `цель ${compact(target)}`, position: "insideEndTop" as const, color: t.muted, fontSize: 10 } }] : []),
        ...(deadlineYm ? [{ xAxis: deadlineYm, label: { formatter: "дедлайн", position: "insideStartTop" as const, color: t.muted, fontSize: 10 } }] : []),
    ];

    let series: EChartsOption["series"] = [
        {
            name: "Накоплено", type: "line", smooth: false,
            showSymbol: actual.filter(v => v != null).length === 1,
            symbolSize: 6, lineStyle: { width: 2.5 }, color,
            areaStyle: { opacity: 0.12, color },
            data: actual,
            markLine: markLineData.length ? {
                silent: true, symbol: "none",
                lineStyle: { color: t.muted, type: "solid", width: 1, opacity: 0.6 },
                data: markLineData,
            } : undefined,
        },
    ];
    if (eta) {
        series.push({
            name: "Прогноз", type: "line", smooth: false, showSymbol: false,
            lineStyle: { width: 2, type: "dashed", color: t.muted }, color: t.muted,
            data: eta,
        });
    }

    const option: EChartsOption = {
        backgroundColor: "transparent",
        grid: { left: 8, right: 18, top: eta ? 30 : 16, bottom: 4, containLabel: true },
        legend: eta ? { top: 0, textStyle: { color: t.muted }, icon: "roundRect", data: ["Накоплено", "Прогноз"] } : undefined,
        tooltip: { trigger: "axis", valueFormatter: v => (v == null ? "—" : fmt(Number(v))) },
        xAxis: {
            type: "category", data: months, boundaryGap: false,
            axisLabel: { color: t.muted, formatter: monthLabel }, axisLine: { lineStyle: { color: t.border } }, axisTick: { show: false },
        },
        yAxis: {
            type: "value", min: 0, max: yMax || undefined,
            axisLabel: { color: t.muted, formatter: (v: number) => compact(v) }, splitLine: { lineStyle: { color: t.border, opacity: 0.5 } },
        },
        series,
    };

    return (
        <div className="card p-5 space-y-2">
            <h2 className="font-medium">Прогресс</h2>
            <ReactECharts option={option} style={{ height: 280 }} notMerge />
            {eta && etaEndYm && (
                <p className="text-xs text-muted-foreground">
                    При текущем темпе ≈ {fmt(Math.round(rate))}/мес — цель к {monthLabel(etaEndYm)}
                    {deadlineYm && (cmpYm(etaEndYm, deadlineYm) <= 0
                        ? <span className="text-positive"> · в срок</span>
                        : <span className="text-destructive"> · позже дедлайна</span>)}
                </p>
            )}
            {etaTooFar && (
                <p className="text-xs text-muted-foreground">
                    При текущем темпе ≈ {fmt(Math.round(rate))}/мес — цель далеко (&gt;{Math.round(HORIZON_CAP_MONTHS / 12)} лет)
                </p>
            )}
            {etaBlockedByRates && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                    Прогноз недоступен: на последний месяц нет курса — накопленное посчитано не полностью
                </p>
            )}
        </div>
    );
}
